import React from 'react'
import ReactDOM from 'react-dom/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { RouterProvider, createRouter } from '@tanstack/react-router'
import { routeTree } from './routeTree.gen'
import { ApiError, onApiError } from './lib/api'
import { NotFound } from './routes/__root'
// Ctrl/⌘ K search providers (workspaces, memories) register on import.
import './shell/paletteProviders'
import './styles.css'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 15_000,
      refetchOnWindowFocus: false,
      // Never retry what retrying cannot fix.
      retry: (count, err) => !(err instanceof ApiError && err.status > 0 && err.status < 500) && count < 2,
    },
    mutations: { retry: false },
  },
})

const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 0,
  scrollRestoration: true,
  defaultNotFoundComponent: NotFound,
})

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

// A session that ends while the console is open (expired, revoked, signed out
// elsewhere) sends the person to the "signed out" page, keeping where they were.
let redirecting = false
onApiError(e => {
  if (redirecting) return
  // The organisation requires two-step sign-in and this person has none yet.
  if (e.code === 'PRECONDITION_FAILED' && (e.detail as { reason?: string } | undefined)?.reason === 'mfa_setup_required') {
    if (!window.location.pathname.startsWith('/me/security')) {
      redirecting = true
      window.location.assign('/me/security?enrol=1&required=1')
    }
    return
  }
  if (!e.isUnauthenticated) return
  // Only a session that EXISTED can expire; a first visit goes to /login via _app.
  if (!queryClient.getQueryData(['session'])) return
  const path = window.location.pathname
  if (['/login', '/signup', '/mfa', '/session-expired', '/verify-email', '/forgot-password', '/reset-password', '/accept-invite', '/sso', '/legal', '/status', '/maintenance', '/choose-org', '/create-org'].some(p => path.startsWith(p))) return
  redirecting = true
  queryClient.clear()
  window.location.assign(`/session-expired?next=${encodeURIComponent(path + window.location.search)}`)
})

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </React.StrictMode>,
)
